import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  input,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { MessageApiService, MessageThread, TaskStatisItem, TaskUnreadClearedEvent } from '../../message.api.service';
import { TaskStatisStore } from '../task-message-list/task-statis.store';
import { MessageSummaryComponent } from '../message-summary/message-summary.component';
import { MessageTypePipe, EvaluateColorPipe, ClassNameTailPipe } from '../../message.pipe';
import { EmjioIconNamePipe } from '../emjio-icon-name.pipe';
import { MessageItemKindPipe } from '../task-message-list/task-message-question-nos.component';
import { NzDividerComponent, NzDividerModule } from 'ng-zorro-antd/divider';
import { Pipe, PipeTransform } from '@angular/core';
import { truncateString, PercentFixedPipe, FlIconComponent } from '@common';

/** 模块名称 Pipe：最多 20 字，超长截断（复用 @common truncateString）；空值兜底「模块」 */
@Pipe({ name: 'messageModuleName', standalone: true })
export class MessageModuleNamePipe implements PipeTransform {
  transform(name: string | null | undefined): string {
    const text = truncateString(name ?? '', 20, '…');
    return text || '模块';
  }
}

/**
 * 行尾类型角标排序 Pipe：固定顺序 我来讲 → 不懂 → 其余（稳定排序，其余类型保持原数组内序）
 */
@Pipe({ name: 'messageTypeOrder', standalone: true })
export class MessageTypeOrderPipe implements PipeTransform {
  private static readonly ORDER = new Map<string, number>([
    ['我来讲', 0],
    ['不懂', 1],
  ]);

  transform(list: TaskStatisItem['typeStatis']): TaskStatisItem['typeStatis'] {
    const rank = (t: { typeName: string }) => MessageTypeOrderPipe.ORDER.get(t.typeName) ?? 2;
    return [...list].sort((a, b) => rank(a) - rank(b)); // 稳定：同 rank 保持原序
  }
}

/**
 * 任务消息聚合条目（左栏单个任务）
 *
 * 渲染「任务条目标题 + 其展开的题号反馈列表（题号 / 得分率 / 评论数）」，自管本任务 statis 的拉取与加载态。
 * 展开/收起由父级通过 expanded 互斥控制；点击条目抛 toggle，点击某题抛 selectQuestion(index) 驱动右栏消息流。
 * 展开区带 height 展开收起动画。
 */
@Component({
  selector: 'fl-task-message-item',
  templateUrl: './task-message-item.component.html',
  standalone: true,
  host: { class: 'block' },
  imports: [
    NgClass,
    NzBadgeModule,
    NzTooltipModule,
    MessageSummaryComponent,
    MessageTypePipe,
    EvaluateColorPipe,
    ClassNameTailPipe,
    MessageItemKindPipe,
    MessageModuleNamePipe,
    MessageTypeOrderPipe,
    NzDividerModule,
    PercentFixedPipe,
    FlIconComponent,
    EmjioIconNamePipe,
  ],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*' })),
      ]),
      transition(':leave', [
        style({ height: '*', overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0' })),
      ]),
    ]),
  ],
})
export class TaskMessageItemComponent implements OnChanges {
  /** 当前任务 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() thread!: MessageThread;

  /** 父级互斥控制是否展开 */
  readonly expanded = input(false);

  /** 是否为左栏列表第一个任务条目：最顶部任务上方无需上分割线 */
  readonly first = input(false);

  /** 右栏当前选中的题 thread_id（TaskStatisItem.id）；行高亮用它匹配，不依赖数组索引 */
  readonly selectedThreadId = input<number | null>(null);

  /** 父级通知本任务某题已读（扣减 statis 未读数） */
  readonly uncleared = input<TaskUnreadClearedEvent>(undefined);

  /** 父级「刷新」序号：变化时重拉本任务 statis 并写回共享 store，右栏随之联动 */
  readonly refreshTick = input(0);

  /** 点条目：父级切换展开 */
  readonly toggle = output<void>();

  /** 点某题：父级驱动右栏 */
  readonly selectQuestion = output<number>();

  /** 展开首拉 statis 后，上报本次应默认定位的题 thread_id（首个有未读的题，全已读则第一题） */
  readonly defaultQuestion = output<number>();

  /** 本任务 statis 题号反馈列表 */
  statis: TaskStatisItem[] = [];

  /** 是否已成功拉取过（幂等，避免反复展开重复请求；就绪后才渲染展开区并触发动画） */
  statisLoaded = false;

  /** 当前应高亮的题 thread_id：外部指定优先，未指定（展开默认）则取第一行 */
  get activeItemId(): number | undefined {
    return this.selectedThreadId() ?? this.statis[0]?.id;
  }

  private messageApiSrv = inject(MessageApiService);
  private statisStore = inject(TaskStatisStore);
  private el = inject(ElementRef<HTMLElement>);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges) {
    // 展开：首拉 statis 后定位默认题；已加载过则基于现有 statis 直接定位，保证折叠后再展开也跳到未读题
    const expanded = this.expanded();
    if (changes['expanded'] && expanded) {
      if (!this.statisLoaded) {
        this.loadStatis();
      } else {
        // 已加载过：不再重新请求。仅当 store 为空（0 题/失败任务）时补一次广播，
        // 否则右栏「折叠后切走再切回」新建实例会因无信号而 loading 卡死；
        // store 已有数据时右栏 applyStatis 能读到并初始化，无需广播。
        if (this.statisStore.get(this.thread.taskId).length === 0) {
          this.statisStore.load(this.thread.taskId, this.statis);
        }
        this.emitDefaultQuestion();
      }
    }
    // 已读扣减：statis 中 threadId 匹配项减未读数
    const uncleared = this.uncleared();
    if (changes['uncleared'] && uncleared) {
      const { threadId, clearedCount } = uncleared;
      this.statis = this.statis.map(s =>
        s.id === threadId ? { ...s, unReadCount: Math.max(0, s.unReadCount - clearedCount) } : s
      );
    }
    // 父级刷新：重拉本任务 statis（覆盖 statisLoaded 幂等），写回 store 后右栏随动
    if (changes['refreshTick'] && expanded) {
      this.loadStatis();
    }
  }

  private async loadStatis() {
    try {
      const list = await this.messageApiSrv.getTaskStatis(this.thread.taskId);
      // 按 thread_id 去重：后端偶发把同一题/模块返回两份，幂等过滤避免左栏题号列表重复展示
      this.statis = list.filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i);
      this.emitDefaultQuestion();
    } finally {
      // 无论成败都置为已加载：成功有列表；失败 statis 为空则走 @empty 显示「暂无题号」，
      // 避免首次展开永远卡在 loading。成败都在此广播一次（成功为真实数据、失败为空数组），
      // 让右栏结束 spinner 走对应状态；成功时 try 内不再重复广播，避免消息流被双拉。
      this.statisLoaded = true;
      this.statisStore.load(this.thread.taskId, this.statis);
      // OnPush：await 续体非模板事件，题号列表与加载态需主动标记
      this.cdr.markForCheck();
    }
  }

  /** 挑「首个有未读的题」（最后有消息），全已读回退第一题，上报默认定位题 thread_id */
  private emitDefaultQuestion() {
    const defaultId = (this.statis.find(s => s.unReadCount > 0) ?? this.statis[0])?.id;
    if (defaultId != null) {
      this.defaultQuestion.emit(defaultId);
    }
  }

  /** 手风琴展开动画结束：把高亮题号行滚入可视区（仅当其在滚动容器外时才滚动，不扰动已可见位置） */
  onExpandDone() {
    if (!this.expanded()) return;
    const target = this.el.nativeElement.querySelector(`[data-item-id="${this.activeItemId}"]`);
    target?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}
